import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { LuFileText } from "react-icons/lu";
import { FiClock } from "react-icons/fi";
import LuFilePen from "../../../../../assets/images/cms/scheduler/LuFilePen card.svg";
import { Col, Row } from 'react-bootstrap';
import Loading from "../../../../utils/loader";
import { getDraftListFilter } from "../../../../../../store/actions/cms/scheduler";
import { NoDataFound } from '../../../../utils/noDataFound';

const DraftList = ({ setShowDraftsList, currentPage, setCurrentPage, setDraftLength }) => {
  const dispatch = useDispatch();
  const { draftListData, draftListLoading, draftListError } = useSelector((state) => {
    return (state?.contentSchedularReducer);
  });

  const [data, setData] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [activeButtons, setActiveButtons] = useState({});
  const [nextCursor, setNextCursor] = useState(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Ref for the intersection observer target
  const loaderRef = useRef(null);
  const observerRef = useRef(null);
  const isInitialLoadRef = useRef(true);
  const isFetchingRef = useRef(false);

  // Handle Redux state changes
  useEffect(() => {
    if (draftListData && !draftListLoading && !draftListError) {

      if (currentPage ===1 ) {
        // Initial load only
        console.log('Initial load - setting data');
        setData(draftListData?.results);
        isInitialLoadRef.current = false;
      } if (currentPage!=1) {
        // Append new data for pagination
        console.log('Loading more - appending data');
        setData(prevData => [...prevData, ...draftListData?.results]);
        setIsLoadingMore(false);
      }

      // Update pagination state based on cursor
      setHasMore(!!draftListData.next);
      setNextCursor(draftListData.next);

      // Reset fetching flag
      isFetchingRef.current = false;
    }
  }, [draftListData, draftListLoading, draftListError, isLoadingMore]);

  // Dispatch action for fetching data
  const fetchData = useCallback((cursor = null) => {
    // Prevent duplicate fetches
    if (isFetchingRef.current) {
      console.log('Already fetching, skipping...');
      return;
    }

    isFetchingRef.current = true;
    let FilterApplied = JSON.parse(localStorage.getItem('filterApplied')) || {};
    
    // If cursor is provided, extract it from the URL or use it directly
    if (cursor) {
      // console.log('Fetching with cursor:', cursor);
      // If cursor is a full URL, extract the cursor parameter
      if (typeof cursor === 'string' && cursor.includes('cursor=')) {
        const url = new URL(cursor);
        const cursorParam = url.searchParams.get('cursor');
        dispatch(getDraftListFilter({ ...FilterApplied, cursor: cursorParam }));
      } else {
        dispatch(getDraftListFilter({ ...FilterApplied, cursor }));
      }
    } else {
      // Initial load without cursor
      console.log('Initial fetch without cursor');
      dispatch(getDraftListFilter({ ...FilterApplied }));
    }
  }, [dispatch]);

  // Load more data when intersection observer triggers
  const loadMore = useCallback(() => {
    if (!draftListLoading && hasMore && nextCursor && !isFetchingRef.current) {
      console.log('Load more triggered');
      setIsLoadingMore(true);
      fetchData(nextCursor);
    }
  }, [draftListLoading, hasMore, nextCursor, fetchData]);

  // Set up intersection observer
  useEffect(() => {
    const options = {
      root: null,
      rootMargin: '20px',
      threshold: 0.1
    };

    observerRef.current = new IntersectionObserver((entries) => {
      const target = entries[0];
      if (target.isIntersecting) {
        setCurrentPage(2)
        loadMore();
      }
    }, options);

    if (loaderRef.current) {
      observerRef.current.observe(loaderRef.current);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [loadMore]);

  // Initial data load - ONLY RUNS ONCE
  useEffect(() => {
    console.log('Component mounted - fetching initial data');
    fetchData();
    
    return () => {
      localStorage?.removeItem('filterApplied');
    };
  }, []); // Empty dependency array - runs only once on mount

  const handleActive = (draft, type) => {
    setActiveButtons((prev) => ({
      ...prev,
      [draft?.entity_id]: type,
    }));
    if (type === 'edit') {
      window?.open(draft?.draft_edit_url, '_blank');
    }
    if (type === 'view') {
      setShowDraftsList({ content_id: draft?.entity_id, content_type: draft?.content_type });
    }
  };

  const retry = () => {
    isFetchingRef.current = false;
    if (data.length === 0) {
      isInitialLoadRef.current = true;
      fetchData();
    } else {
      setIsLoadingMore(true);
      fetchData(nextCursor);
    }
  };

  useEffect(() => {
    if (data) {
      setDraftLength(data?.length);
    }
  }, [data, setDraftLength]);

  return (
    <div className="draft-container scheduler_custom_container">
      {/* Draft Items Grid */}
      {(!data.length && draftListLoading) && (
        <div className="flex justify-center items-center py-8">
          <Loading className="draft_loader" />
        </div>
      )}
      <div className="">
        <Row>
          {data?.map((item) => (
            <Col md={4} lg={3} sm={6} key={item?.id} style={{ marginBottom: '20px' }}>
              <div className="draft_block">
                <div className="draft_block_head">
                  <div className="draft_icon_1">
                    <LuFileText size={16} />
                  </div>
                  <div className="draft_type">
                    <span className="draft_type_span">TYPE</span>
                    <p>{item?.content_type?.charAt(0).toUpperCase() + item?.content_type?.slice(1)}</p>
                  </div>
                  <div className="draft_icon_2">
                    <img src={LuFilePen} alt="Draft icon" />
                  </div>
                  <div className="draft_number">
                    <span className="draft_type_span">DRAFTS</span>
                    <p>{item?.no_of_drafts}</p>
                  </div>
                </div>
                <div className='draft_block_body'>
                  <svg className='draft_bg_svg' width="104" height="54" viewBox="0 0 104 54" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="70.5" cy="70.5" r="64.5" stroke="url(#6kvfxinara)" strokeWidth="12" />
                    <defs>
                      <linearGradient id="6kvfxinara" x1="70" y1="0" x2="46.5" y2="70" gradientUnits="userSpaceOnUse">
                        <stop stopColor="#F3DEEF" stopOpacity=".7" />
                        <stop offset="1" stopColor="#F8E1EE" stopOpacity="0" />
                      </linearGradient>
                    </defs>
                  </svg>
                  <div className='draft_block_top'>
                    <h3 className='draft_block_content'>{item?.content_title?.charAt(0).toUpperCase() + item?.content_title?.slice(1)}</h3>
                    <div className='draft_updated'>
                      <FiClock className='draft_clock_svg' />
                      <p>Updated By {item?.author_name?.charAt(0).toUpperCase() + item?.author_name?.slice(1)} {item?.last_created}</p>
                    </div>
                  </div>
                  <div className='draft_block_buttons'>
                    {item?.draft_edit_url && (
                      <button
                        className={"draft_button"}
                        onClick={() => handleActive(item, "edit")}
                      >
                        Edit Draft
                      </button>
                    )}
                    {(item?.has_published_version) &&
                      <button
                        className={`draft_button`}
                        onClick={() => handleActive(item, "view")}
                        disabled={!item?.no_of_drafts || item?.no_of_drafts === 0}
                      >
                        View Draft Versions
                      </button>
                    }
                  </div>
                </div>
              </div>
            </Col>
          ))}
        </Row>
      </div>

      {/* Loading/Error States */}
      {draftListLoading && data.length > 0 && (
        <div className="flex justify-center items-center py-8">
          <Loading className="draft_loader" />
        </div>
      )}

      {draftListError && (
        <div className="flex flex-col items-center py-8">
          <p className="text-red-600 mb-4">Error loading data: {JSON.stringify(draftListError)}</p>
          <button
            onClick={retry}
            className="reschedule"
          >
            Retry
          </button>
        </div>
      )}

      {(!draftListLoading && draftListData?.results?.length === 0) && (
        <div className="my-5 ">
          <NoDataFound />
        </div>
      )}

      {/* Intersection Observer Target */}
      {hasMore && !draftListError && (
        <div ref={loaderRef} className="h-10 flex items-center justify-center">
          {/* This div serves as the intersection target */}
        </div>
      )}
    </div>
  );
};

export default DraftList;